// useSlashCommands.js — Loads available slash commands from
// /api/slash-commands and exposes filter + execute helpers.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';

// Detects the slash-command token the user is ACTIVELY typing — anchored at
// the END of the current line. So it fires whether `/cmd` is at the very start
// ("/brainstorm") OR mid-message ("corrige isso /brain") — antes só abria no
// começo da mensagem (bug: digitar "/cmd" depois de texto não abria o popup).
//
// Guards:
//  • A barra precisa estar no início da linha ou após espaço → caminhos como
//    "src/foo" ou "cd src/" NUNCA disparam (a barra vem grudada em letra).
//  • Lone `/` → name '/' → abre o catálogo inteiro.
//  • Assim que o usuário digita um espaço após o token, o regex não casa mais
//    → o popup fecha. Isso também habilita chain: digitar nova `/` reabre.
const ACTIVE_SLASH_REGEX = /(?:^|\s)\/([a-z0-9:_-]*)$/i;

export function detectSlashPrefix(input, caret) {
  if (typeof input !== 'string') return null;
  // Caret-aware (bug do Pedro 08-17): a detecção só olha o texto ATÉ o cursor,
  // então um `/skill` digitado NO MEIO da frase — com texto DEPOIS do cursor —
  // dispara igual. Antes o regex era ancorado no fim do texto INTEIRO (`$`), então
  // qualquer coisa após o token matava o popup. `caret` omitido → texto inteiro
  // (retrocompatível: chamadas antigas e o caso "token no fim" seguem idênticos).
  const upto = (typeof caret === 'number' && caret >= 0 && caret <= input.length)
    ? input.slice(0, caret)
    : input;
  // Opera só na linha atual (drafts com Shift+Enter podem ter várias linhas).
  const lastNewline = upto.lastIndexOf('\n');
  const line = lastNewline === -1 ? upto : upto.slice(lastNewline + 1);
  const match = line.match(ACTIVE_SLASH_REGEX);
  if (!match) return null;
  const name = '/' + (match[1] || '');
  return {
    name,
    args: '',
    full: name,
    // O token ativo está, por definição, sendo digitado — nunca "complete".
    // (Um espaço depois dele faz o regex falhar → detecção null → popup some,
    // mesmo efeito que a antiga flag `complete` tinha.)
    complete: false,
  };
}

// Faixa [start, end) do /token ATIVO que termina no cursor: `start` = índice da
// barra, `end` = cursor. null quando não há token ativo. Os pick handlers usam
// isso pra trocar EXATAMENTE o token digitado preservando o texto DEPOIS do
// cursor (senão o /copy do meio da frase ficaria órfão ao virar pill). Mesma
// gramática do ACTIVE_SLASH_REGEX (barra no início da linha ou após espaço).
export function activeSlashTokenRange(text, caret) {
  if (typeof text !== 'string') return null;
  const c = (typeof caret === 'number' && caret >= 0 && caret <= text.length) ? caret : text.length;
  const upto = text.slice(0, c);
  const lineStart = upto.lastIndexOf('\n') + 1; // 0 quando não há \n
  const line = upto.slice(lineStart);
  const m = line.match(/(^|\s)(\/[a-z0-9:_-]*)$/i);
  if (!m) return null;
  const start = lineStart + m.index + m[1].length; // pula o (^|\s) antes da barra
  return { start, end: c };
}

const END_ANCHORED_TOKEN = /(^|\s)\/[a-z0-9:_-]*$/i;

// Remove o /token (skill vira PILL nas abas Code/Chat-workspace) preservando o
// texto DEPOIS do cursor e colapsando o espaço da junção. `range` vem do
// activeSlashTokenRange; sem ele, fallback end-anchored na linha atual (retro).
// Retorna { text, caret } — caret onde o token estava.
export function removeSlashToken(text, range) {
  const src = typeof text === 'string' ? text : '';
  if (range && range.start >= 0 && range.end <= src.length && range.start <= range.end) {
    let before = src.slice(0, range.start);
    const after = src.slice(range.end);
    if (before.endsWith(' ') && after.startsWith(' ')) before = before.slice(0, -1);
    return { text: before + after, caret: before.length };
  }
  const lastNewline = src.lastIndexOf('\n');
  const prefix = lastNewline === -1 ? '' : src.slice(0, lastNewline + 1);
  const line = lastNewline === -1 ? src : src.slice(lastNewline + 1);
  const next = prefix + line.replace(END_ANCHORED_TOKEN, '$1');
  return { text: next, caret: next.length };
}

// Insere `command` + espaço no lugar do /token (Home NÃO usa pills — o comando
// entra inline no texto). Preserva o texto depois do cursor. Retorna { text,
// caret } com o cursor logo após o comando inserido.
export function insertSlashToken(text, range, command) {
  const src = typeof text === 'string' ? text : '';
  const ins = `${command} `;
  if (range && range.start >= 0 && range.end <= src.length && range.start <= range.end) {
    const before = src.slice(0, range.start);
    let after = src.slice(range.end);
    if (after.startsWith(' ')) after = after.slice(1); // evita espaço duplo pós-inserção
    return { text: before + ins + after, caret: (before + ins).length };
  }
  const lastNewline = src.lastIndexOf('\n');
  const prefix = lastNewline === -1 ? '' : src.slice(0, lastNewline + 1);
  const line = lastNewline === -1 ? src : src.slice(lastNewline + 1);
  const next = prefix + line.replace(END_ANCHORED_TOKEN, `$1${ins}`);
  return { text: next, caret: next.length };
}

export function useSlashCommands() {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // SessionComposer.stop.test (and any fast unmount) used to hit
  // "window is not defined" after jsdom teardown: apiFetch resolved and
  // setError/setLoading ran on an unmounted tree. Skip setState when gone.
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoading(true);
    const res = await apiFetch('/api/slash-commands');
    if (!mountedRef.current) return;
    if (res.ok) {
      setCommands(res.data?.commands || []);
      setError(null);
    } else {
      setError(res.error || 'Failed to load slash commands');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    // Refetch when a plugin is installed/removed so new "/" commands appear
    // without an app reload. PluginsView dispatches this after install/delete.
    const onChanged = () => refresh();
    window.addEventListener('apolo:plugins-changed', onChanged);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('apolo:plugins-changed', onChanged);
    };
  }, [refresh]);

  const filter = useCallback(
    (query) => {
      if (!query) return commands;
      const q = query.toLowerCase().replace(/^\//, '');
      return commands.filter(
        (c) =>
          c.slash_command.toLowerCase().includes(q) ||
          c.display_name.toLowerCase().includes(q) ||
          (c.description || '').toLowerCase().includes(q) ||
          c.plugin_slug.toLowerCase().includes(q),
      );
    },
    [commands],
  );

  const execute = useCallback(async (input) => {
    return apiFetch('/api/slash-commands/execute', {
      method: 'POST',
      body: { input },
    });
  }, []);

  const resolve = useCallback(async (input) => {
    return apiFetch('/api/slash-commands/resolve', {
      method: 'POST',
      body: { input },
    });
  }, []);

  return {
    commands,
    loading,
    error,
    refresh,
    filter,
    execute,
    resolve,
  };
}

// Ranking do "/" (bug "o / parou de funcionar"): o filtro era SUBSTRING puro em 3
// campos — digitar "t" casava quase o catálogo inteiro via "/accessibility-agenTs"
// e a ordem alfabética ENTERRAVA o alvo (/tastedesign nem aparecia no top-8).
// Agora: prefixo do COMANDO (o que o usuário digita) vence, depois prefixo do
// nome, depois substring, depois plugin. Escore menor = melhor; empate mantém
// a ordem do catálogo (estável).
export function scoreSlashCommand(c, q) {
  const slash = String(c.slash_command || '').toLowerCase();
  const name = String(c.display_name || '').toLowerCase();
  const flat = slash.replace(/^\//, ''); // "plugin:skill" ou "skill"
  const tail = flat.includes(':') ? flat.slice(flat.indexOf(':') + 1) : flat;
  if (tail.startsWith(q) || flat.startsWith(q)) return 0; // prefixo do comando
  if (name.startsWith(q)) return 1; // prefixo do nome visível
  if (slash.includes(q) || name.includes(q)) return 2; // substring
  if (String(c.plugin_slug || '').toLowerCase().includes(q)) return 3;
  return 4; // não casa
}

export function useFilteredCommands(input, caret) {
  const { commands, loading, error, execute } = useSlashCommands();
  const detected = useMemo(() => detectSlashPrefix(input || '', caret), [input, caret]);
  // Sprint v8.3 — esconde popup quando user já completou a seleção (há char
  // depois do /cmd). Permite chain: digitar nova `/` reabre popup pra
  // próxima skill.
  const filtered = useMemo(() => {
    if (!detected || detected.complete) return [];
    const q = detected.name.toLowerCase().replace(/^\//, '');
    if (!q) return commands.slice(0, 50);
    return commands
      .map((c, i) => ({ c, i, s: scoreSlashCommand(c, q) }))
      .filter((x) => x.s < 4)
      .sort((a, b) => a.s - b.s || a.i - b.i)
      .map((x) => x.c)
      .slice(0, 50);
  }, [commands, detected]);
  return {
    commands: filtered,
    detected,
    loading,
    error,
    execute,
  };
}
